<main>
  <loading-overlay [isLoading]="isAnyReportLoading()"></loading-overlay>

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ 'ProductionReporting.title' | translateWF: 'Production Reports' }}</h1>
      </div>
    </div>

    @if (canViewGeneralReports) {
      <p-panel [header]="'Knitting'" [toggleable]="true">
        <p-panel [header]="'ProductionReporting.knitting_report_title' | translateWF: 'Knitting Production Report'" data-tone="surface">
          <ng-template pTemplate="icons">
            <p-button
              [label]="'ProductionReporting.generate_report' | translateWF: 'Generate Report'"
              icon="pi pi-download"
              (onClick)="downloadKnittingReport()"
              [outlined]="true"
              [loading]="loadingKnittingReport() > 0"
              [disabled]="!knittingForm.valid || isAnyReportLoading()"
            />
          </ng-template>
          <p-card>
            <form [formGroup]="knittingForm">
              <div class="grid-auto-4">
                <div class="form-box">
                  <label for="knitting-from-date">{{ 'ProductionReporting.from_date' | translateWF: 'From Date' }} *</label>
                  <p-datepicker
                    id="knitting-from-date"
                    formControlName="fromDate"
                    [iconDisplay]="'input'"
                    [showIcon]="true"
                    [showTime]="true"
                    dateFormat="yy-mm-dd"
                    fluid
                    class="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': knittingForm.get('fromDate')?.invalid && knittingForm.get('fromDate')?.touched }"
                  />
                  <small class="p-error" *ngIf="knittingForm.get('fromDate')?.invalid && knittingForm.get('fromDate')?.touched">
                    {{ 'ProductionReporting.from_date_required' | translateWF: 'From date is required' }}
                  </small>
                </div>

                <div class="form-box">
                  <label for="knitting-to-date">{{ 'ProductionReporting.to_date' | translateWF: 'To Date' }} *</label>
                  <p-datepicker
                    id="knitting-to-date"
                    formControlName="toDate"
                    [iconDisplay]="'input'"
                    [showIcon]="true"
                    [showTime]="true"
                    dateFormat="yy-mm-dd"
                    fluid
                    class="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': knittingForm.get('toDate')?.invalid && knittingForm.get('toDate')?.touched }"
                  />
                  <small class="p-error" *ngIf="knittingForm.get('toDate')?.invalid && knittingForm.get('toDate')?.touched">
                    {{ 'ProductionReporting.to_date_required' | translateWF: 'To date is required' }}
                  </small>
                </div>
              </div>
            </form>
          </p-card>
          <ng-template pTemplate="footer">
            <p class="report-description">
              {{
                'ProductionReporting.knitting_report_description'
                  | translateWF
                    : 'Generate and download the knitting
            production report for a specific date range'
              }}
            </p>
          </ng-template>
        </p-panel>
      </p-panel>

      <p-panel [header]="'Dyeing'" [toggleable]="true">
        <p-panel [header]="'ProductionReporting.chemical_consumption_title' | translateWF: 'Chemical Consumption'" data-tone="surface">
          <ng-template pTemplate="icons">
            <p-button
              [label]="'ProductionReporting.generate_report' | translateWF: 'Generate Report'"
              icon="pi pi-download"
              (onClick)="downloadChemicalConsumptionReport()"
              [outlined]="true"
              [loading]="loadingChemicalConsumption() > 0"
              [disabled]="!chemicalConsumptionForm.valid || isAnyReportLoading()"
            />
          </ng-template>
          <p-card>
            <form [formGroup]="chemicalConsumptionForm">
              <div class="grid-auto-4">
                <div class="form-box">
                  <label for="chemical-year">{{ 'ProductionReporting.year' | translateWF: 'Year' }} *</label>
                  <p-select
                    id="chemical-year"
                    formControlName="year"
                    [options]="yearOptions"
                    optionLabel="label"
                    optionValue="value"
                    [placeholder]="'ProductionReporting.select_year' | translateWF: 'Select year'"
                    fluid
                    class="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': chemicalConsumptionForm.get('year')?.invalid && chemicalConsumptionForm.get('year')?.touched }"
                  />
                  <small class="p-error" *ngIf="chemicalConsumptionForm.get('year')?.invalid && chemicalConsumptionForm.get('year')?.touched">
                    {{ 'ProductionReporting.year_required' | translateWF: 'Year is required' }}
                  </small>
                </div>

                <div class="form-box">
                  <label for="chemical-month">{{ 'ProductionReporting.month' | translateWF: 'Month' }} *</label>
                  <p-select
                    id="chemical-month"
                    formControlName="month"
                    [options]="monthOptions"
                    optionLabel="label"
                    optionValue="value"
                    [placeholder]="'ProductionReporting.select_month' | translateWF: 'Select month'"
                    fluid
                    class="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': chemicalConsumptionForm.get('month')?.invalid && chemicalConsumptionForm.get('month')?.touched }"
                  />
                  <small class="p-error" *ngIf="chemicalConsumptionForm.get('month')?.invalid && chemicalConsumptionForm.get('month')?.touched">
                    {{ 'ProductionReporting.month_required' | translateWF: 'Month is required' }}
                  </small>
                </div>

                <div class="form-box flex items-end">
                  <div class="flex items-center gap-2">
                    <p-checkbox formControlName="includeAllConsumptions" [binary]="true" inputId="chemical-include-all" />
                    <label for="chemical-include-all">
                      {{
                        'ProductionReporting.include_all_consumptions'
                          | translateWF
                            : 'Show Finishing Routes without
                    completed consumption'
                      }}
                    </label>
                  </div>
                </div>
              </div>
            </form>
          </p-card>
          <ng-template pTemplate="footer">
            <p class="report-description">
              {{
                'ProductionReporting.chemical_consumption_description'
                  | translateWF
                    : 'Generate and download the
            chemical consumption report for a specific month'
              }}
            </p>
          </ng-template>
        </p-panel>
      </p-panel>

      <p-panel [header]="'Machine Assignment'" [toggleable]="true">
        <p-panel [header]="'ProductionReporting.machine_assignment_title' | translateWF: 'Machine Assignment'" data-tone="surface">
          <ng-template pTemplate="icons">
            <p-button
              [label]="'ProductionReporting.generate_report' | translateWF: 'Generate Report'"
              icon="pi pi-download"
              (onClick)="downloadMachineAssignmentReport()"
              [outlined]="true"
              [loading]="loadingMachineAssignment() > 0"
              [disabled]="!machineAssignmentForm.valid || isAnyReportLoading()"
            />
          </ng-template>
          <p-card>
            <form [formGroup]="machineAssignmentForm">
              <div class="grid-auto-4">
                <div class="form-box">
                  <label for="machine-year">{{ 'ProductionReporting.year' | translateWF: 'Year' }} *</label>
                  <p-select
                    id="machine-year"
                    formControlName="year"
                    [options]="yearOptions"
                    optionLabel="label"
                    optionValue="value"
                    [placeholder]="'ProductionReporting.select_year' | translateWF: 'Select year'"
                    fluid
                    class="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': machineAssignmentForm.get('year')?.invalid && machineAssignmentForm.get('year')?.touched }"
                  />
                  <small class="p-error" *ngIf="machineAssignmentForm.get('year')?.invalid && machineAssignmentForm.get('year')?.touched">
                    {{ 'ProductionReporting.year_required' | translateWF: 'Year is required' }}
                  </small>
                </div>

                <div class="form-box">
                  <label for="machine-month">{{ 'ProductionReporting.month' | translateWF: 'Month' }} *</label>
                  <p-select
                    id="machine-month"
                    formControlName="month"
                    [options]="monthOptions"
                    optionLabel="label"
                    optionValue="value"
                    [placeholder]="'ProductionReporting.select_month' | translateWF: 'Select month'"
                    fluid
                    class="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': machineAssignmentForm.get('month')?.invalid && machineAssignmentForm.get('month')?.touched }"
                  />
                  <small class="p-error" *ngIf="machineAssignmentForm.get('month')?.invalid && machineAssignmentForm.get('month')?.touched">
                    {{ 'ProductionReporting.month_required' | translateWF: 'Month is required' }}
                  </small>
                </div>
              </div>
            </form>
          </p-card>
          <ng-template pTemplate="footer">
            <p class="report-description">
              {{
                'ProductionReporting.machine_assignment_description'
                  | translateWF
                    : 'Generate and download the machine
            assignment report for a specific month'
              }}
            </p>
          </ng-template>
        </p-panel>
      </p-panel>
    }

    @if (canViewDailyProduction) {
      <p-panel [header]="'ProductionReporting.daily_production_title' | translateWF: 'Daily Production'" [toggleable]="true">
        <ng-template pTemplate="icons">
          <p-button
            [label]="'ProductionReporting.generate_report' | translateWF: 'Generate Report'"
            icon="pi pi-download"
            (onClick)="downloadDailyProductionReport()"
            [outlined]="true"
            [loading]="loadingDailyProduction() > 0"
            [disabled]="!dailyProductionForm.valid || isAnyReportLoading()"
          />
        </ng-template>
        <p-card>
          <form [formGroup]="dailyProductionForm">
            <div class="grid-auto-4">
              <div class="form-box">
                <label for="daily-production-from-date">{{ 'ProductionReporting.from_date' | translateWF: 'From Date' }} *</label>
                <p-datepicker
                  id="daily-production-from-date"
                  formControlName="fromDate"
                  [iconDisplay]="'input'"
                  [showIcon]="true"
                  [showTime]="true"
                  dateFormat="yy-mm-dd"
                  fluid
                  class="w-full"
                  [ngClass]="{ 'ng-invalid ng-dirty': dailyProductionForm.get('fromDate')?.invalid && dailyProductionForm.get('fromDate')?.touched }"
                />
                <small class="p-error" *ngIf="dailyProductionForm.get('fromDate')?.invalid && dailyProductionForm.get('fromDate')?.touched">
                  {{ 'ProductionReporting.from_date_required' | translateWF: 'From date is required' }}
                </small>
              </div>

              <div class="form-box">
                <label for="daily-production-to-date">{{ 'ProductionReporting.to_date' | translateWF: 'To Date' }} *</label>
                <p-datepicker
                  id="daily-production-to-date"
                  formControlName="toDate"
                  [iconDisplay]="'input'"
                  [showIcon]="true"
                  [showTime]="true"
                  dateFormat="yy-mm-dd"
                  fluid
                  class="w-full"
                  [ngClass]="{ 'ng-invalid ng-dirty': dailyProductionForm.get('toDate')?.invalid && dailyProductionForm.get('toDate')?.touched }"
                />
                <small class="p-error" *ngIf="dailyProductionForm.get('toDate')?.invalid && dailyProductionForm.get('toDate')?.touched">
                  {{ 'ProductionReporting.to_date_required' | translateWF: 'To date is required' }}
                </small>
              </div>

              <div class="form-box">
                <label for="daily-production-processes">{{ 'ProductionReporting.processes' | translateWF: 'Processes' }}</label>
                <p-multiSelect
                  id="daily-production-processes"
                  formControlName="processes"
                  [options]="dailyProductionProcessOptions"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="'ProductionReporting.select_processes' | translateWF: 'All processes'"
                  [showToggleAll]="true"
                  display="chip"
                  fluid
                  class="w-full"
                />
                <small class="report-hint">
                  {{ 'ProductionReporting.processes_hint' | translateWF: 'Leave empty to include every process' }}
                </small>
              </div>
            </div>
          </form>
        </p-card>
        <ng-template pTemplate="footer">
          <p class="report-description">
            {{
              'ProductionReporting.machine_assignment_description'
                | translateWF
                  : 'Generate and download the machine
          assignment report for a specific month'
            }}
          </p>
        </ng-template>
      </p-panel>
    }

    @if (canViewProductionDetail) {
      <p-panel [header]="'ProductionReporting.production_detail_title' | translateWF: 'Area Production Detail'" [toggleable]="true">
        <ng-template pTemplate="icons">
          <p-button
            [label]="'ProductionReporting.generate_report' | translateWF: 'Generate Report'"
            icon="pi pi-download"
            (onClick)="downloadProductionDetailReport()"
            [outlined]="true"
            [loading]="loadingProductionDetail() > 0"
            [disabled]="!productionDetailForm.valid || isAnyReportLoading()"
          />
        </ng-template>
        <p-card>
          <form [formGroup]="productionDetailForm">
            <div class="grid-auto-4">
              <div class="form-box">
                <label for="production-detail-from-date">{{ 'ProductionReporting.from_date' | translateWF: 'From Date' }} *</label>
                <p-datepicker
                  id="production-detail-from-date"
                  formControlName="fromDate"
                  [iconDisplay]="'input'"
                  [showIcon]="true"
                  [showTime]="true"
                  dateFormat="yy-mm-dd"
                  fluid
                  class="w-full"
                  [ngClass]="{ 'ng-invalid ng-dirty': productionDetailForm.get('fromDate')?.invalid && productionDetailForm.get('fromDate')?.touched }"
                />
                <small class="p-error" *ngIf="productionDetailForm.get('fromDate')?.invalid && productionDetailForm.get('fromDate')?.touched">
                  {{ 'ProductionReporting.from_date_required' | translateWF: 'From date is required' }}
                </small>
              </div>
              <div class="form-box">
                <label for="production-detail-to-date">{{ 'ProductionReporting.to_date' | translateWF: 'To Date' }} *</label>
                <p-datepicker
                  id="production-detail-to-date"
                  formControlName="toDate"
                  [iconDisplay]="'input'"
                  [showIcon]="true"
                  [showTime]="true"
                  dateFormat="yy-mm-dd"
                  fluid
                  class="w-full"
                  [ngClass]="{ 'ng-invalid ng-dirty': productionDetailForm.get('toDate')?.invalid && productionDetailForm.get('toDate')?.touched }"
                />
                <small class="p-error" *ngIf="productionDetailForm.get('toDate')?.invalid && productionDetailForm.get('toDate')?.touched">
                  {{ 'ProductionReporting.to_date_required' | translateWF: 'To date is required' }}
                </small>
              </div>
              <div class="form-box">
                <label for="production-detail-processes">{{ 'ProductionReporting.areas' | translateWF: 'Areas' }}</label>
                <p-multiSelect
                  id="production-detail-processes"
                  formControlName="processes"
                  [options]="productionDetailProcessOptions"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="'ProductionReporting.select_areas' | translateWF: 'All areas'"
                  [showToggleAll]="true"
                  display="chip"
                  fluid
                  class="w-full"
                />
                <small class="report-hint">
                  {{
                    'ProductionReporting.areas_hint'
                      | translateWF
                        : 'Leave empty to include every area you have access
                to'
                  }}
                </small>
              </div>
            </div>
          </form>
        </p-card>
        <ng-template pTemplate="footer">
          <p class="report-description">
            {{
              'ProductionReporting.production_detail_description'
                | translateWF
                  : 'Generate and download the roll-by-roll production detail report by area for a specific date
          range'
            }}
          </p>
        </ng-template>
      </p-panel>
    }
  </div>
</main>
